// src/pages/Staff.js
/**
 * Full Staff.js — Complete staff management page (≈1000+ lines)
 *
 * - Full staff table (GET /api/users/get-user)
 * - Add / Edit / Delete staff (POST /api/users/register, PUT /api/users/edit-staff, POST /api/users/delete-staff)
 * - Upload images (POST /api/image/upload) — used for table upload and camera captures
 * - Camera modal with:
 *     • automatic start when modal opens (with small delay)
 *     • manual Start Webcam / Stop Webcam controls
 *     • Snapshot (preview)
 *     • Capture & Upload which converts frame -> Blob -> File and uploads
 * - Access Key update modal
 * - Robust error handling, logging, and helpful UI messages
 *
 * NOTE: This file is long and intentionally verbose (extended comments and clear sections)
 * so it's easy to copy & paste into your project as a single-file replacement.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import DefaultLayout from "../components/DefaultLayout";
import CloseButton from "../components/CloseButton";
import config from "../config";

import axios from "axios";
import Swal from "sweetalert2";

import {
  DeleteOutlined,
  EditOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
  KeyOutlined,
  UploadOutlined,
  InfoCircleOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
  CameraOutlined,
  StopOutlined,
  CheckOutlined,
} from "@ant-design/icons";

import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Checkbox,
  Upload,
  Tooltip,
  Tag,
  Space,
  Divider,
  Row,
  Col,
  Skeleton,
  Empty,
} from "antd";

/* ==========================================================================
   Utilities
   ========================================================================== */

/** Add cache-busting timestamp to image URL so updated images don't get cached */
const addCacheBuster = (url) => (url ? `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}` : url);

/** Extract filename from URL or fallback to a default name */
const filenameFromUrl = (url) => {
  if (!url) return "image";
  try {
    const u = new URL(url);
    const parts = u.pathname.split("/");
    return parts.pop() || "image";
  } catch {
    const parts = `${url}`.split("/");
    return parts.pop() || "image";
  }
};

/** Read axios error message in a friendly way */
const getAxiosErrorMessage = (err, fallback = "An error occurred") =>
  err?.response?.data?.message || err?.message || fallback;

/** Simple logger */
const log = (...args) => {
  try {
    // eslint-disable-next-line no-console
    console.log("[Staff]", ...args);
  } catch (e) {
    // ignore
  }
};

/** Sleep / wait helper */
const wait = (ms) => new Promise((res) => setTimeout(res, ms));

/* ==========================================================================
   Main Component
   ========================================================================== */

const Staff = () => {
  const apiBaseUrl = config.REACT_APP_API_BASE_URL;

  /* ------------------------------
     State & refs
     ------------------------------ */

  // Staff list
  const [staffData, setStaffData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  // Popup modal (add / edit)
  const [popupModal, setPopupModal] = useState(false);
  const [editStaff, setEditStaff] = useState(null);

  // Modal upload state
  const [fileListForModal, setFileListForModal] = useState([]);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // Camera modal state & refs
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const videoRef = useRef(null);
  const captureCanvasRef = useRef(null);
  const cameraStreamRef = useRef(null);

  // Snapshot preview
  const [snapshotPreviewUrl, setSnapshotPreviewUrl] = useState(null);

  // Manual camera control state
  const [webcamStarting, setWebcamStarting] = useState(false);
  const [webcamStarted, setWebcamStarted] = useState(false);

  // Access key modal
  const [editAccessKeyPopupModal, setEditAccessKeyPopupModal] = useState(false);
  const [editAccessKey, setEditAccessKey] = useState(null);

  // General UI
  const [loading, setLoading] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const [form] = Form.useForm();

  // Mounted flag to avoid state updates after unmount
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // cleanup camera if page unloads
      try {
        const s = cameraStreamRef.current;
        if (s) s.getTracks().forEach((t) => t.stop());
      } catch (e) {}
    };
  }, []);

  /* ------------------------------
     Fetch staff list
     ------------------------------ */

  const getALLStaff = async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${apiBaseUrl}/api/users/get-user`);
      const data = Array.isArray(res.data) ? res.data : [];
      setStaffData(data);
      if (searchKeyword) {
        const lower = searchKeyword.toLowerCase();
        setFilteredData(
          data.filter((r) => {
            const name = (r.name || "").toLowerCase();
            const uid = (r.userId || "").toLowerCase();
            const role = (r.role || "").toLowerCase();
            return name.includes(lower) || uid.includes(lower) || role.includes(lower);
          })
        );
      } else {
        setFilteredData(data);
      }
    } catch (err) {
      console.error("getALLStaff error", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch staff data",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      if (mountedRef.current) setLoadingList(false);
    }
  };

  useEffect(() => {
    getALLStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------------------------
     Search
     ------------------------------ */

  const handleSearch = (searchValue) => {
    setSearchKeyword(searchValue || "");
    const lowerSearch = (searchValue || "").toLowerCase();
    if (!searchValue) {
      setFilteredData(staffData);
      return;
    }
    const filtered = staffData.filter((r) => {
      const name = (r.name || "").toLowerCase();
      const uid = (r.userId || "").toLowerCase();
      const role = (r.role || "").toLowerCase();
      return name.includes(lowerSearch) || uid.includes(lowerSearch) || role.includes(lowerSearch);
    });
    setFilteredData(filtered);
  };

  /* ------------------------------
     Delete
     ------------------------------ */

  const handledDelete = async (record) => {
    try {
      await axios.post(`${apiBaseUrl}/api/users/delete-staff`, { usersId: record._id });

      Swal.fire({
        title: "Success",
        text: "Staff member deleted successfully",
        icon: "success",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2500,
      });

      setStaffData((prev) => prev.filter((u) => u._id !== record._id));
      setFilteredData((prev) => prev.filter((u) => u._id !== record._id));
    } catch (err) {
      console.error("handledDelete error", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete staff member",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  /* ------------------------------
     Upload helper (server)
     ------------------------------ */

  const uploadImageToS3 = async (file) => {
    const actualFile = file?.originFileObj || file;
    if (!actualFile) throw new Error("No file provided for upload");

    try {
      const formData = new FormData();
      formData.append("image", actualFile);

      const response = await axios.post(`${apiBaseUrl}/api/image/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response?.data?.url) {
        message.success("Image uploaded successfully!");
        return response.data.url;
      } else if (response?.data?.filename) {
        const filename = response.data.filename;
        const potentialUrl = `${apiBaseUrl.replace(/\/$/, "")}/uploads/${filename}`;
        message.success("Image uploaded successfully!");
        return potentialUrl;
      } else {
        throw new Error("Invalid upload response (missing url)");
      }
    } catch (error) {
      console.error("uploadImageToS3 error:", error);
      throw new Error(getAxiosErrorMessage(error, "Image upload failed. Please try again."));
    }
  };

  /* ------------------------------
     Table-level (row) upload
     ------------------------------ */

  const handleTableUpload = async (file, record) => {
    try {
      message.loading({ content: "Uploading image...", key: "uploadRow" });
      const url = await uploadImageToS3(file);

      await axios.put(`${apiBaseUrl}/api/users/edit-staff`, {
        usersId: record._id,
        image: url,
      });

      message.success({ content: "Image uploaded & saved", key: "uploadRow", duration: 2 });

      setStaffData((prev) =>
        prev.map((u) => (u._id === record._id || u.userId === record.userId ? { ...u, image: url } : u))
      );
      setFilteredData((prev) =>
        prev.map((u) => (u._id === record._id || u.userId === record.userId ? { ...u, image: url } : u))
      );
    } catch (err) {
      console.error("handleTableUpload error", err);
      message.error(getAxiosErrorMessage(err, "Failed to upload image"));
    }
    return false;
  };

  /* ------------------------------
     Modal image upload handler
     ------------------------------ */

  const handleModalImageUpload = async (file) => {
    try {
      setIsUploadingImage(true);
      message.loading({ content: "Uploading image...", key: "modalUpload" });

      const url = await uploadImageToS3(file);
      setUploadedImageUrl(url);

      const previewObj = {
        uid: file.uid || `${Date.now()}`,
        name: filenameFromUrl(url),
        status: "done",
        url: addCacheBuster(url),
      };
      setFileListForModal([previewObj]);

      message.success({ content: "Image uploaded", key: "modalUpload", duration: 2 });
    } catch (err) {
      console.error("handleModalImageUpload error", err);
      message.error({ content: getAxiosErrorMessage(err, "Upload failed"), key: "modalUpload", duration: 4 });
      setUploadedImageUrl(null);
      setFileListForModal([]);
    } finally {
      setIsUploadingImage(false);
    }
    return false;
  };

  const handleModalRemoveImage = () => {
    setFileListForModal([]);
    setUploadedImageUrl(null);
    setSnapshotPreviewUrl(null);
  };

  /* =========================================================================
     Camera helpers (webcam): startWebcam, stopCamera, takeSnapshot, captureAndUpload
     ========================================================================= */

  /** Open camera modal (resets snapshot preview) */
  const openCameraModal = async () => {
    setSnapshotPreviewUrl(null);
    setCameraModalOpen(true);
  };

  /** Start webcam — sets video.srcObject and plays */
  const startWebcam = async () => {
    const v = videoRef.current;
    if (!v) return;
    setWebcamStarting(true);
    try {
      log("🎥 Requesting webcam access (startWebcam)...");
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480 },
        audio: false,
      });
      v.srcObject = stream;
      cameraStreamRef.current = stream;
      v.muted = true;
      try {
        await v.play();
      } catch (playErr) {
        // some browsers may reject autoplay; video can still display when user interacts
        console.warn("startWebcam play failed", playErr);
      }
      setWebcamStarted(true);
      log("✅ Webcam started successfully (startWebcam)");
    } catch (err) {
      console.error("webcam error", err);
      message.error("Please allow webcam access in your browser settings.");
      setWebcamStarted(false);
      throw err;
    } finally {
      setWebcamStarting(false);
    }
  };

  /** Stop camera and cleanup */
  const stopCamera = () => {
    const s = cameraStreamRef.current;
    if (s) {
      try {
        s.getTracks().forEach((t) => t.stop());
      } catch (e) {
        // ignore
      }
      cameraStreamRef.current = null;
    }
    try {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      }
    } catch (ignore) {}
    setWebcamStarted(false);
    log("Camera stopped");
  };

  /**
   * Take a snapshot from the live video and set preview(s).
   * - sets snapshotPreviewUrl (data URL)
   * - adds blob preview to fileListForModal so UI shows the preview consistently
   */
  const takeSnapshot = () => {
    if (!videoRef.current) {
      message.error("Camera not ready");
      return;
    }
    const video = videoRef.current;
    const canvas = captureCanvasRef.current || document.createElement("canvas");
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // fast preview as data URL
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    setSnapshotPreviewUrl(dataUrl);

    // also create a blob URL for preview in fileListForModal (consistent behavior)
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const tmpUrl = URL.createObjectURL(blob);
        setFileListForModal([
          {
            uid: `snapshot-${Date.now()}`,
            name: `snapshot.jpg`,
            status: "done",
            url: tmpUrl,
          },
        ]);
      },
      "image/jpeg",
      0.9
    );
  };

  /**
   * Capture current frame and upload to server.
   * Stops camera after upload and sets uploadedImageUrl so Save uses it.
   */
  const captureAndUpload = async () => {
    if (!videoRef.current) {
      message.error("Camera not ready");
      return;
    }

    const video = videoRef.current;
    const canvas = captureCanvasRef.current || document.createElement("canvas");
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      async (blob) => {
        if (!blob) {
          message.error("Failed to capture image");
          return;
        }
        const file = new File([blob], `capture_${Date.now()}.jpg`, { type: "image/jpeg" });

        // stop camera immediately — user expects modal to close
        stopCamera();
        setCameraModalOpen(false);

        // upload
        setIsUploadingImage(true);
        message.loading({ content: "Uploading captured photo...", key: "captureUpload" });
        try {
          const url = await uploadImageToS3(file);
          setUploadedImageUrl(url);
          setFileListForModal([
            {
              uid: `capture-${Date.now()}`,
              name: filenameFromUrl(url),
              status: "done",
              url: addCacheBuster(url),
            },
          ]);
          setSnapshotPreviewUrl(addCacheBuster(url));
          message.success({ content: "Captured photo uploaded", key: "captureUpload", duration: 2 });
        } catch (err) {
          console.error("captureAndUpload upload error", err);
          message.error(getAxiosErrorMessage(err, "Failed to upload captured photo"));
        } finally {
          setIsUploadingImage(false);
        }
      },
      "image/jpeg",
      0.9
    );
  };

  /* ------------------------------
     Helpers for add/edit payloads
     ------------------------------ */

  const filterEditData = (record) => {
    return {
      _id: record._id,
      name: record.name || "",
      userId: record.userId || "",
      role: record.role || "staff",
      accessKey: record.accessKey || "",
      image: record.image || null,
      password: "",
    };
  };

  /* ------------------------------
     Submit Add / Edit staff
     ------------------------------ */

  const handleSubmit = async (values) => {
    setLoading(true);

    // prefer uploadedImageUrl if user uploaded or captured photo in modal
    const imageToSend = uploadedImageUrl || (editStaff && editStaff.image) || "";

    const payload = {
      ...values,
      image: imageToSend,
    };

    try {
      if (!editStaff) {
        await axios.post(`${apiBaseUrl}/api/users/register`, payload);

        Swal.fire({
          title: "Success",
          text: "Staff member registered successfully",
          icon: "success",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2500,
        });

        setUploadedImageUrl(null);
        setFileListForModal([]);
        setPopupModal(false);
        form.resetFields();
        await getALLStaff();
      } else {
        await axios.put(`${apiBaseUrl}/api/users/edit-staff`, {
          ...payload,
          usersId: editStaff._id,
        });

        Swal.fire({
          title: "Success",
          text: "Staff member updated successfully",
          icon: "success",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2500,
        });

        setStaffData((prev) =>
          prev.map((u) => (u._id === editStaff._id || u.userId === editStaff.userId ? { ...u, ...payload } : u))
        );
        setFilteredData((prev) =>
          prev.map((u) => (u._id === editStaff._id || u.userId === editStaff.userId ? { ...u, ...payload } : u))
        );

        setEditStaff(null);
        setUploadedImageUrl(null);
        setFileListForModal([]);
        setPopupModal(false);
      }
    } catch (err) {
      console.error("handleSubmit error", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: getAxiosErrorMessage(err, "Failed to save staff member"),
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  /* ------------------------------
     Edit Access Key flow
     ------------------------------ */

  const handleEditAccessKey = async (values) => {
    setLoading(true);
    try {
      await axios.post(`${apiBaseUrl}/api/users/update-access-key`, {
        newAccessKey: values.accessKey,
        userId: editAccessKey.userId,
      });

      Swal.fire({
        title: "Success",
        text: "Access Key updated successfully",
        icon: "success",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2500,
      });

      setStaffData((prev) =>
        prev.map((u) => (u.userId === editAccessKey.userId ? { ...u, accessKey: values.accessKey } : u))
      );
      setFilteredData((prev) =>
        prev.map((u) => (u.userId === editAccessKey.userId ? { ...u, accessKey: values.accessKey } : u))
      );

      setEditAccessKey(null);
      setEditAccessKeyPopupModal(false);
    } catch (err) {
      console.error("handleEditAccessKey error", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: getAxiosErrorMessage(err, "Failed to update access key"),
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  /* ------------------------------
     Cancel / Reset flows
     ------------------------------ */

  const handleCancel = () => {
    if (loading) {
      Swal.fire({
        icon: "warning",
        title: "Please wait",
        text: "Proceed submitting...",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }

    setPopupModal(false);
    setEditStaff(null);
    setEditAccessKeyPopupModal(false);
    setEditAccessKey(null);
    setUploadedImageUrl(null);
    setFileListForModal([]);
    setSnapshotPreviewUrl(null);
    form.resetFields();
  };

  const handleRoleDropdownVisibleChange = (open) => setRoleOpen(open);

  /* ------------------------------
     Table columns
     ------------------------------ */

  const columns = useMemo(
    () => [
      {
        title: "Image",
        dataIndex: "image",
        key: "image",
        width: 120,
        render: (url) => {
          if (!url) {
            return <div style={{ color: "#999", fontSize: 12 }}>No Image</div>;
          }
          return (
            <img
              src={addCacheBuster(url)}
              alt="avatar"
              style={{ width: 50, height: 50, objectFit: "cover", borderRadius: "50%", border: "1px solid #eee" }}
            />
          );
        },
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        width: "25%",
        render: (text) => <div style={{ wordBreak: "break-all", whiteSpace: "normal" }}>{text}</div>,
        sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
        showSorterTooltip: false,
      },
      {
        title: "ID",
        dataIndex: "userId",
        key: "userId",
        width: "20%",
        render: (text) => <div style={{ wordBreak: "break-all", whiteSpace: "normal" }}>{text}</div>,
        sorter: (a, b) => (a.userId || "").localeCompare(b.userId || ""),
        showSorterTooltip: false,
      },
      {
        title: "Role",
        dataIndex: "role",
        key: "role",
        width: "15%",
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
          <div style={{ padding: 8 }}>
            <Checkbox.Group
              style={{ display: "flex", flexDirection: "column" }}
              options={["admin", "staff"].map((role) => ({ label: role.charAt(0).toUpperCase() + role.slice(1), value: role }))}
              value={selectedKeys}
              onChange={(keys) => {
                setSelectedKeys(keys);
                confirm({ closeDropdown: false });
              }}
            />
            <Button onClick={() => { clearFilters(); confirm(); }} size="small" style={{ width: "100%", marginTop: 8 }}>
              Reset
            </Button>
          </div>
        ),
        onFilter: (value, record) => record.role === value,
        render: (role) => <Tag color={role === "admin" ? "blue" : "default"}>{role?.toUpperCase()}</Tag>,
      },
      {
        title: "Action",
        dataIndex: "_id",
        key: "action",
        width: "28%",
        render: (id, record) => (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Tooltip title="Edit Access Key">
              <KeyOutlined
                style={{ cursor: "pointer", fontSize: 18, marginLeft: 6 }}
                onClick={() => {
                  setEditAccessKey(record);
                  setEditAccessKeyPopupModal(true);
                }}
              />
            </Tooltip>

            <Tooltip title="Edit Staff">
              <EditOutlined
                style={{ cursor: "pointer", fontSize: 18, marginLeft: 6 }}
                onClick={() => {
                  const editData = filterEditData(record);
                  setEditStaff(editData);

                  if (record.image) {
                    setFileListForModal([
                      {
                        uid: "preview",
                        name: filenameFromUrl(record.image),
                        status: "done",
                        url: addCacheBuster(record.image),
                      },
                    ]);
                  } else {
                    setFileListForModal([]);
                  }

                  setUploadedImageUrl(null);

                  form.setFieldsValue({
                    name: record.name,
                    userId: record.userId,
                    role: record.role,
                    accessKey: record.accessKey,
                  });

                  setPopupModal(true);
                }}
              />
            </Tooltip>

            <Popconfirm title="Are you sure you want to delete this record?" onConfirm={() => handledDelete(record)} okText="Yes" cancelText="No" placement="left">
              <DeleteOutlined style={{ cursor: "pointer", fontSize: 18, color: "red", marginLeft: 6 }} />
            </Popconfirm>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [staffData]
  );

  /* ------------------------------
     Cosmetic styles
     ------------------------------ */

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .ant-input,
      .ant-picker,
      .ant-upload-list-picture .ant-upload {
        border-radius: 5px;
        font-size: 1rem;
        padding: 6.6px 12px;
        border: 1px solid #ddd;
        width: 100%;
        box-sizing: border-box;
        transition: border-color 0.3s ease;
      }
      .staff-page-wrapper .page-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .staff-page-wrapper .page-title {
        margin: 0;
      }
      .blur-background {
        filter: blur(2px);
        pointer-events: none;
        user-select: none;
      }
      .upload-button {
        display: inline-flex;
        align-items: center;
        gap: 8px;
      }
      .camera-preview {
        width: 100%;
        border-radius: 8px;
        background: #000;
      }
      .snapshot-preview {
        width: 120px;
        height: 120px;
        object-fit: cover;
        border-radius: 8px;
        border: 1px solid #eee;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  /* ------------------------------
     Render
     ------------------------------ */

  return (
    <DefaultLayout searchPlaceholder="Name / ID / Role" onSearch={handleSearch}>
      <div className={`staff-page-wrapper${popupModal || editAccessKeyPopupModal ? " blur-background" : ""}`}>
        <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h1 className="page-title">Staff Members</h1>
          <Space>
            <Tooltip title="Refresh">
              <Button icon={<SyncOutlined spin={loadingList} />} onClick={() => getALLStaff()} />
            </Tooltip>
            <Button
              type="primary"
              onClick={() => {
                setEditStaff(null);
                setUploadedImageUrl(null);
                setFileListForModal([]);
                form.resetFields();
                form.setFieldsValue({
                  name: "",
                  userId: "",
                  password: "",
                  accessKey: "",
                  role: "staff",
                });
                form.setFieldsValue({ role: "staff" });
                setPopupModal(true);
              }}
            >
              Add Staff
            </Button>
          </Space>
        </div>

        <Divider style={{ margin: "12px 0" }} />

        <Table
          columns={columns}
          dataSource={filteredData}
          bordered
          loading={loadingList}
          rowKey={(r) => r._id}
          locale={{
            emptyText: loadingList ? <Skeleton active paragraph={{ rows: 2 }} /> : <Empty />,
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            pageSizeOptions: ["10", "20", "50", "100"],
            showSizeChanger: filteredData.length > 10,
            showTotal: (total, range) => `Showing ${range[0]}–${range[1]} of ${total} items`,
            onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
          }}
        />
      </div>

      {/* Add / Edit Modal */}
      {popupModal && (
        <Modal
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {editStaff ? "Edit Staff" : "Add New Staff"}
              <CloseButton onClick={handleCancel} />
            </div>
          }
          open={popupModal}
          onCancel={handleCancel}
          footer={false}
          destroyOnClose
        >
          <Form layout="vertical" form={form} initialValues={editStaff || { role: "staff" }} onFinish={handleSubmit}>
            <Form.Item
              name="name"
              label={
                <span>
                  Name <Tooltip title="Full name for identification"><InfoCircleOutlined /></Tooltip>
                </span>
              }
              rules={[{ required: true, message: "Please enter the name" }, { min: 3, message: "Name must be at least 3 characters" }]}
            >
              <Input />
            </Form.Item>

            {!editStaff && (
              <Form.Item
                name="userId"
                label={
                  <span>
                    User ID <Tooltip title="Unique staff ID"><InfoCircleOutlined /></Tooltip>
                  </span>
                }
                rules={[
                  { required: true, message: "Please enter Staff ID" },
                  { min: 3, message: "ID must be at least 3 characters" },
                  {
                    validator: (_, value) => {
                      const isDuplicate = staffData.some((s) => s.userId === value);
                      if (isDuplicate) return Promise.reject(new Error("This user id already exists!"));
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input />
              </Form.Item>
            )}

            {!editStaff && (
              <Form.Item
                name="password"
                label={
                  <span>
                    Password <Tooltip title="Min 6 chars, with upper/lower/number/special"><InfoCircleOutlined /></Tooltip>
                  </span>
                }
                rules={[
                  { required: true, message: "Please enter your password" },
                  { min: 6, message: "Password must be at least 6 characters long" },
                  { pattern: /[A-Z]/, message: "Password must include at least one uppercase letter" },
                  { pattern: /[a-z]/, message: "Password must include at least one lowercase letter" },
                  { pattern: /[0-9]/, message: "Password must include at least one number" },
                  { pattern: /[!@#$%^&*]/, message: "Password must include at least one special character (!@#$%^&*)" },
                ]}
              >
                <Input.Password />
              </Form.Item>
            )}

            {!editStaff && (
              <Form.Item
                name="accessKey"
                label={
                  <span>
                    Access Key <Tooltip title="Exactly 4 digits"><InfoCircleOutlined /></Tooltip>
                  </span>
                }
                rules={[{ required: true, message: "Please enter the access key" }, { pattern: /^\d{4}$/, message: "Access key must be exactly 4 digits" }]}
              >
                <Input.Password maxLength={4} inputMode="numeric" pattern="\d*" placeholder="Enter 4-digit access key" />
              </Form.Item>
            )}

            <Form.Item name="role" label="Role" rules={[{ required: true, message: "Please select role" }]}>
              <Select onDropdownVisibleChange={handleRoleDropdownVisibleChange} suffixIcon={roleOpen ? <CaretUpOutlined /> : <CaretDownOutlined />}>
                <Select.Option value="admin">Admin</Select.Option>
                <Select.Option value="staff">Staff</Select.Option>
              </Select>
            </Form.Item>

            {/* Image upload inside modal */}
            <Form.Item
              label={
                <span>
                  Profile Image <Tooltip title="Optional. Upload a square image for best look."><InfoCircleOutlined /></Tooltip>
                </span>
              }
            >
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={handleModalImageUpload}
                  onRemove={handleModalRemoveImage}
                  fileList={fileListForModal}
                  listType="picture"
                >
                  <Button icon={<UploadOutlined />} disabled={isUploadingImage}>
                    {isUploadingImage ? "Uploading..." : "Upload Image"}
                  </Button>
                </Upload>

                <Button icon={<CameraOutlined />} onClick={() => { setSnapshotPreviewUrl(null); setCameraModalOpen(true); }} disabled={isUploadingImage}>
                  Take Photo
                </Button>

                <div style={{ minWidth: 90 }}>
                  {snapshotPreviewUrl ? (
                    <img src={addCacheBuster(snapshotPreviewUrl)} alt="preview" style={{ marginTop: 10, width: 80, height: 80, objectFit: "cover", borderRadius: "50%", border: "1px solid #eee" }} />
                  ) : uploadedImageUrl ? (
                    <img src={addCacheBuster(uploadedImageUrl)} alt="preview" style={{ marginTop: 10, width: 80, height: 80, objectFit: "cover", borderRadius: "50%", border: "1px solid #eee" }} />
                  ) : editStaff?.image ? (
                    <img src={addCacheBuster(editStaff.image)} alt="preview" style={{ marginTop: 10, width: 80, height: 80, objectFit: "cover", borderRadius: "50%", border: "1px solid #eee" }} />
                  ) : null}
                </div>
              </div>
            </Form.Item>

            <div className="d-flex justify-content-end ant-modal-footer" style={{ marginTop: 12 }}>
              <Button onClick={handleCancel} style={{ marginRight: 8 }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading} disabled={loading}>
                Save
              </Button>
            </div>
          </Form>
        </Modal>
      )}

      {/* Camera capture modal */}
      <Modal
        title="Take Photo"
        open={cameraModalOpen}
        onCancel={() => {
          stopCamera();
          setCameraModalOpen(false);
        }}
        footer={[
          <Button key="stop" icon={<StopOutlined />} onClick={() => { stopCamera(); setCameraModalOpen(false); }}>
            Stop
          </Button>,
          <Button key="snapshot" icon={<CheckOutlined />} onClick={takeSnapshot} disabled={!webcamStarted} loading={webcamStarting}>
            Snapshot
          </Button>,
          <Button key="capture" type="primary" onClick={captureAndUpload} loading={isUploadingImage} disabled={!webcamStarted}>
            Capture & Upload
          </Button>,
        ]}
        width={720}
        destroyOnClose
        afterOpenChange={(visible) => {
          if (visible) {
            // start camera automatically after modal animation, but allow manual start if user prefers
            setTimeout(async () => {
              try {
                await startWebcam();
              } catch (err) {
                console.warn("startWebcam failed", err);
              }
            }, 400);
          } else {
            stopCamera();
          }
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ background: "#000", borderRadius: 8, overflow: "hidden", minHeight: 240 }}>
            <video ref={videoRef} className="camera-preview" autoPlay muted playsInline style={{ width: "100%", height: "auto", display: "block", background: "#000" }} />
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", gap: 8 }}>
              <Button onClick={startWebcam} loading={webcamStarting} disabled={webcamStarted}>
                Start Webcam
              </Button>
              <Button
                onClick={() => {
                  stopCamera();
                  setSnapshotPreviewUrl(null);
                  setFileListForModal([]);
                }}
                disabled={!webcamStarted}
              >
                Stop Webcam
              </Button>
              <Button onClick={takeSnapshot} disabled={!webcamStarted}>
                Snapshot (Preview)
              </Button>
              <Button danger onClick={() => setFileListForModal([])}>
                Clear Preview
              </Button>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {snapshotPreviewUrl && <img src={snapshotPreviewUrl} alt="snapshot" className="snapshot-preview" />}
              {fileListForModal[0] && <img src={fileListForModal[0].url} alt="preview" className="snapshot-preview" />}
            </div>
          </div>

          <canvas ref={captureCanvasRef} style={{ display: "none" }} />
        </div>
      </Modal>

      {/* Edit Access Key Modal */}
      {editAccessKeyPopupModal && (
        <Modal
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              Update Access Key <CloseButton onClick={handleCancel} />
            </div>
          }
          open={editAccessKeyPopupModal}
          onCancel={handleCancel}
          footer={false}
          destroyOnClose
        >
          <Form layout="vertical" onFinish={handleEditAccessKey}>
            <Form.Item
              name="accessKey"
              label={
                <span>
                  Access Key <Tooltip title="Exactly 4 digits"><InfoCircleOutlined /></Tooltip>
                </span>
              }
              rules={[{ required: true, message: "Please enter the access key" }, { pattern: /^\d{4}$/, message: "Access key must be exactly 4 digits" }]}
            >
              <Input.Password maxLength={4} inputMode="numeric" pattern="\d*" placeholder="Enter 4-digit access key" />
            </Form.Item>

            <div className="d-flex justify-content-end ant-modal-footer" style={{ marginTop: 12 }}>
              <Button onClick={handleCancel} style={{ marginRight: 8 }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading} disabled={loading}>
                Save
              </Button>
            </div>
          </Form>
        </Modal>
      )}
    </DefaultLayout>
  );
};

export default Staff;
